import { collection, query, where, getDocs, doc, getDoc, updateDoc, documentId, arrayUnion } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createNotification } from './notificationsService';
import { Event } from './eventsService';

const normalizePhone = (raw: unknown): string => String(raw ?? '').replace(/\D/g, '');

/**
 * Serviço para enviar notificações relacionadas a eventos
 * Integra com a API Node.js para enviar push notifications
 */

/**
 * Helper para converter date de diferentes fontes para Date nativo
 * Suporta Firestore Timestamp, Date, e string (evitando timezone shift)
 */
const getEventDate = (date: any): Date => {
  if (date?.toDate) return date.toDate(); // Firestore Timestamp
  if (date instanceof Date) return date;
  if (typeof date === 'string') return new Date(date + 'T00:00:00'); // Prevent timezone shift
  return new Date(date);
};

interface GuestContact {
  guestId: string;
  email: string;
  phone: string;
  hostlyUserId: string;
}

const getGuestContactsByIds = async (guestIds: string[]): Promise<GuestContact[]> => {
  if (guestIds.length === 0) return [];

  try {
    const contacts: GuestContact[] = [];
    const guestsRef = collection(db, 'guests');

    const chunks: string[][] = [];
    for (let i = 0; i < guestIds.length; i += 10) {
      chunks.push(guestIds.slice(i, i + 10));
    }

    for (const chunk of chunks) {
      const q = query(guestsRef, where(documentId(), 'in', chunk));
      const snapshot = await getDocs(q);
      snapshot.forEach((d) => {
        const data = d.data();
        contacts.push({
          guestId: d.id,
          email: data.email ? String(data.email).toLowerCase() : '',
          phone: normalizePhone(data.phone),
          hostlyUserId: data.hostlyUserId ? String(data.hostlyUserId) : '',
        });
      });
    }

    return contacts;
  } catch (error) {
    console.error('Erro ao buscar contatos dos guests:', error);
    return [];
  }
};

interface HostlyMatch {
  guestId: string;
  hostlyUserId: string;
}

const resolveHostlyUserIds = async (contacts: GuestContact[]): Promise<HostlyMatch[]> => {
  const matches: HostlyMatch[] = [];
  if (contacts.length === 0) return matches;

  const remaining: GuestContact[] = [];
  for (const c of contacts) {
    if (c.hostlyUserId) {
      matches.push({ guestId: c.guestId, hostlyUserId: c.hostlyUserId });
    } else {
      remaining.push(c);
    }
  }
  if (remaining.length === 0) return matches;

  const usersRef = collection(db, 'users');

  const emails = remaining.map(c => c.email).filter(Boolean);
  if (emails.length > 0) {
    const chunks: string[][] = [];
    for (let i = 0; i < emails.length; i += 10) chunks.push(emails.slice(i, i + 10));
    for (const chunk of chunks) {
      const q = query(usersRef, where('email', 'in', chunk));
      const snap = await getDocs(q);
      snap.forEach((d) => {
        const email = String(d.data().email || '').toLowerCase();
        const guest = remaining.find(c => c.email === email);
        if (guest && !matches.find(m => m.guestId === guest.guestId)) {
          matches.push({ guestId: guest.guestId, hostlyUserId: d.id });
        }
      });
    }
  }

  const stillRemaining = remaining.filter(c => !matches.find(m => m.guestId === c.guestId) && c.phone);
  if (stillRemaining.length > 0) {
    const phones = stillRemaining.map(c => c.phone);
    const chunks: string[][] = [];
    for (let i = 0; i < phones.length; i += 10) chunks.push(phones.slice(i, i + 10));
    for (const chunk of chunks) {
      const q = query(usersRef, where('phoneNormalized', 'in', chunk));
      const snap = await getDocs(q);
      snap.forEach((d) => {
        const phone = normalizePhone(d.data().phoneNormalized);
        const guest = stillRemaining.find(c => c.phone === phone);
        if (guest && !matches.find(m => m.guestId === guest.guestId)) {
          matches.push({ guestId: guest.guestId, hostlyUserId: d.id });
        }
      });
    }
  }

  // Persiste o vínculo no guest doc para próximas notificações
  for (const m of matches) {
    const original = contacts.find(c => c.guestId === m.guestId);
    if (original && !original.hostlyUserId) {
      try {
        await updateDoc(doc(db, 'guests', m.guestId), { hostlyUserId: m.hostlyUserId });
      } catch {
        // não bloqueia notificação se falhar persistir
      }
    }
  }

  return matches;
};

/**
 * Busca informações do usuário criador do evento
 */
const getUserInfo = async (userId: string): Promise<{ name: string; email: string }> => {
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));

    if (userSnap.exists()) {
      const userData = userSnap.data();
      return {
        name: userData.displayName || userData.name || userData.email?.split('@')[0] || 'Alguém',
        email: userData.email || '',
      };
    }

    return { name: 'Alguém', email: '' };
  } catch (error) {
    console.error('Erro ao buscar informações do usuário:', error);
    return { name: 'Alguém', email: '' };
  }
};

/**
 * Envia notificações quando um novo evento é criado
 */
export const notifyEventCreated = async (
  event: Event,
  eventId: string
): Promise<void> => {
  try {
    // Buscar emails dos convidados (guests são armazenados por ID)
    const guestIds = event.guests || [];
    if (guestIds.length === 0) {
      console.log('Nenhum convidado para notificar');
      return;
    }

    const guestContacts = await getGuestContactsByIds(guestIds);
    const matches = await resolveHostlyUserIds(guestContacts);
    const guestUserIds = Array.from(new Set(matches.map(m => m.hostlyUserId)));
    if (guestUserIds.length === 0) {
      console.log('Nenhum convidado é usuário do Hostly');
      return;
    }

    // Dá acesso de leitura vivo ao evento para os convidados (regra Firestore
    // libera get quando o uid está em invitedUserIds). Assim veem info completa,
    // updates em tempo real e o evento some ao ser deletado.
    try {
      await updateDoc(doc(db, 'events', eventId), { invitedUserIds: arrayUnion(...guestUserIds) });
    } catch (e) {
      console.error('Erro ao gravar invitedUserIds no evento:', e);
    }

    // Buscar informações do criador
    const creator = await getUserInfo(event.userId);

    // Formatar data para exibição e para metadata
    let dateStr = '';
    let dateDisplay = '';
    if (event.date) {
      const eventDate = getEventDate(event.date);
      dateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      dateDisplay = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    // Criar notificação in-app no Firestore para cada convidado
    // Backend dispara push automaticamente
    const safeEventTitle = (event.title || '').trim() || 'um evento';
    const title = `Convite: ${safeEventTitle}`;

    const lines: string[] = [];
    lines.push(`${creator.name} te convidou para "${safeEventTitle}".`);
    if (dateDisplay) lines.push(`Data: ${dateDisplay}${event.time ? ` às ${event.time}` : ''}`);
    if (event.location) lines.push(`Local: ${event.location}`);
    lines.push('Abra o Hostly para confirmar presença.');
    const body = lines.join('\n');

    for (const userId of guestUserIds) {
      await createNotification({
        userId,
        type: 'guest_invite',
        title,
        message: body,
        senderId: event.userId,
        senderName: creator.name,
        metadata: {
          eventId,
          creatorId: event.userId,
          creatorName: creator.name,
          eventTitle: safeEventTitle,
          date: dateStr,
          time: event.time || '',
          location: event.location || '',
        },
      });
    }

    console.log(`Notificações in-app criadas para ${guestUserIds.length} usuários`);
  } catch (error) {
    console.error('Erro ao notificar criação de evento:', error);
  }
};

/**
 * Notifica um usuário Hostly que ele foi convidado diretamente (por email)
 * a ver/co-editar um evento. Usado pelo compartilhamento direto do dono.
 */
export const notifyEventShared = async (
  event: Event,
  eventId: string,
  targetUserId: string,
  asEditor: boolean
): Promise<boolean> => {
  try {
    const creator = await getUserInfo(event.userId);
    const safeEventTitle = (event.title || '').trim() || 'um evento';

    let dateStr = '';
    let dateDisplay = '';
    if (event.date) {
      const eventDate = getEventDate(event.date);
      dateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      dateDisplay = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    const lines: string[] = [];
    lines.push(
      asEditor
        ? `${creator.name} te convidou para co-organizar "${safeEventTitle}".`
        : `${creator.name} compartilhou o evento "${safeEventTitle}" com você.`
    );
    if (dateDisplay) lines.push(`Data: ${dateDisplay}${event.time ? ` às ${event.time}` : ''}`);
    if (event.location) lines.push(`Local: ${event.location}`);
    lines.push('Abra o Hostly para ver o evento.');

    await createNotification({
      userId: targetUserId,
      type: 'guest_invite',
      title: `Convite: ${safeEventTitle}`,
      message: lines.join('\n'),
      senderId: event.userId,
      senderName: creator.name,
      metadata: {
        eventId,
        creatorId: event.userId,
        creatorName: creator.name,
        eventTitle: safeEventTitle,
        date: dateStr,
        time: event.time || '',
        location: event.location || '',
        asEditor,
      },
    });
    return true;
  } catch (error) {
    console.error('Erro ao notificar compartilhamento de evento:', error);
    return false;
  }
};

/**
 * Envia notificações quando um evento é atualizado
 */
export const notifyEventUpdated = async (
  eventId: string,
  oldEvent: Event,
  newEvent: Partial<Event>
): Promise<void> => {
  try {
    // Verificar mudanças relevantes
    const changes: string[] = [];

    if (newEvent.title && newEvent.title !== oldEvent.title) {
      changes.push('nome');
    }
    if (newEvent.date && getEventDate(newEvent.date).getTime() !== getEventDate(oldEvent.date).getTime()) {
      changes.push('data');
    }
    if (newEvent.location && newEvent.location !== oldEvent.location) {
      changes.push('local');
    }
    if (newEvent.time && newEvent.time !== oldEvent.time) {
      changes.push('horário');
    }
    if (newEvent.guestItems && JSON.stringify(newEvent.guestItems) !== JSON.stringify(oldEvent.guestItems || {})) {
      changes.push('itens dos convidados');
    }

    // Identifica convidados novos (presentes no novo, ausentes no antigo) para enviar convite a eles
    const oldGuestIds = new Set(oldEvent.guests || []);
    const newGuestIds: string[] = (newEvent.guests || []).filter(g => !oldGuestIds.has(g));

    if (changes.length === 0 && newGuestIds.length === 0) {
      console.log('Nenhuma mudança relevante para notificar');
      return;
    }

    // Convidar novos guests via notifyEventCreated (envia convite completo)
    if (newGuestIds.length > 0) {
      const eventForInvite = {
        ...oldEvent,
        ...newEvent,
        guests: newGuestIds,
      } as Event;
      await notifyEventCreated(eventForInvite, eventId);
    }

    // Se só houve adição de guest, não envia "evento atualizado" para os antigos
    if (changes.length === 0) {
      return;
    }

    // Para mudanças no evento: notificar APENAS convidados antigos (os novos já receberam convite acima)
    const guestIdsForUpdate = (newEvent.guests || oldEvent.guests || []).filter(g => oldGuestIds.has(g));
    if (guestIdsForUpdate.length === 0) {
      console.log('Nenhum convidado antigo para notificar atualização');
      return;
    }

    const guestContacts = await getGuestContactsByIds(guestIdsForUpdate);
    const matches = await resolveHostlyUserIds(guestContacts);
    const guestUserIds = Array.from(new Set(matches.map(m => m.hostlyUserId)));
    if (guestUserIds.length === 0) {
      console.log('Nenhum convidado é usuário do Hostly');
      return;
    }

    // Formatar data atualizada
    const finalDate = newEvent.date || oldEvent.date;
    let dateStr = '';
    if (finalDate) {
      const eventDate = getEventDate(finalDate);
      dateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
    }

    // Criar notificação in-app no Firestore para cada convidado
    // O backend agora dispara o push automaticamente
    const eventTitle = newEvent.title || oldEvent.title || 'um evento';
    const changesText = changes.join(', ');
    const updaterId = newEvent.userId || oldEvent.userId;
    const updater = updaterId ? await getUserInfo(updaterId) : { name: 'Alguém', email: '' };

    const title = `Atualizado: ${eventTitle}`;
    const finalDateForDisplay = newEvent.date || oldEvent.date;
    const dateDisplay = finalDateForDisplay
      ? getEventDate(finalDateForDisplay).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      : '';

    const lines: string[] = [];
    lines.push(`${updater.name} alterou ${changesText} de "${eventTitle}".`);
    if (dateDisplay) {
      const t = newEvent.time || oldEvent.time;
      lines.push(`Data: ${dateDisplay}${t ? ` às ${t}` : ''}`);
    }
    const loc = newEvent.location || oldEvent.location;
    if (loc) lines.push(`Local: ${loc}`);
    lines.push('Abra o Hostly para ver os detalhes.');
    const body = lines.join('\n');

    for (const userId of guestUserIds) {
      await createNotification({
        userId,
        type: 'event_update',
        title,
        message: body,
        senderId: updaterId,
        senderName: updater.name,
        metadata: {
          eventId,
          changes,
          eventTitle,
          date: dateStr,
          time: newEvent.time || oldEvent.time || '',
          location: newEvent.location || oldEvent.location || '',
        },
      });
    }

    console.log(`Notificações de atualização criadas para ${guestUserIds.length} usuários`);
  } catch (error) {
    console.error('Erro ao notificar atualização de evento:', error);
  }
};

/**
 * Envia lembretes de eventos que acontecerão em breve
 * Esta função deve ser chamada periodicamente (ex: via scheduled job)
 */
export const sendEventReminders = async (): Promise<void> => {
  try {
    console.log('Verificando eventos próximos para lembretes...');

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const dayAfterTomorrow = new Date(tomorrow);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

    // Buscar eventos que acontecerão nas próximas 24h
    const eventsRef = collection(db, 'events');
    const eventsSnapshot = await getDocs(eventsRef);

    const upcomingEvents: Array<Event & { id: string }> = [];

    eventsSnapshot.forEach((doc) => {
      const data = doc.data();
      const eventDate = data.date?.toDate();

      if (eventDate && eventDate >= tomorrow && eventDate < dayAfterTomorrow) {
        upcomingEvents.push({
          id: doc.id,
          ...data,
          date: eventDate,
        } as Event & { id: string });
      }
    });

    console.log(`Encontrados ${upcomingEvents.length} eventos para amanhã`);

    for (const event of upcomingEvents) {
      // Buscar convidados
      const guestIds = event.guests || [];
      const guestContacts = await getGuestContactsByIds(guestIds);
      const matches = await resolveHostlyUserIds(guestContacts);
      const guestUserIds = Array.from(new Set(matches.map(m => m.hostlyUserId)));

      // Incluir o criador
      const allUserIds = [...new Set([event.userId, ...guestUserIds])];

      // Criar notificação in-app para cada usuário
      // O backend dispara o push automaticamente
      const title = 'Lembrete de Evento';
      const timeStr = event.time || 'hora não definida';
      const body = `"${event.title}" é amanhã às ${timeStr}!`;

      for (const userId of allUserIds) {
        await createNotification({
          userId,
          type: 'event_update',
          title,
          message: body,
          metadata: { eventId: event.id, reminder: true },
        });
      }

      console.log(`Lembretes criados para evento ${event.id} (Firestore triggered)`);
    }

    console.log('Verificação de lembretes concluída');
  } catch (error) {
    console.error('Erro ao enviar lembretes de eventos:', error);
  }
};
